/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Pure helpers for profile-aware company field updates during registration.
 * @module logic/appointments/appointmentRegistrationFieldChange
 */

import {
  getProfileByNumber,
  isProfileEditableField,
} from "../../utils/common.js";

/**
 * Whether a field value should be treated as missing for requirement tracking.
 * @param {*} value
 * @returns {boolean}
 */
export const isFieldValueMissing = (value) =>
  value === null || value === undefined || value === "";

/**
 * Applies a single field change to company state (profile-aware).
 * Profile 1 updates root; profiles 2/3 update matching `perfiles` entry for editable fields.
 * @param {Object|null} company - Current company snapshot
 * @param {number} selectedProfile - Selected profile index (0 = profile 1)
 * @param {string} field - Field key
 * @param {*} value - New value
 * @returns {Object|null} Updated company or null when `company` is falsy
 */
export const applyFieldChangeToCompany = (
  company,
  selectedProfile,
  field,
  value,
) => {
  if (!company) {
    return null;
  }

  if (selectedProfile > 0 && isProfileEditableField(field)) {
    const profile = getProfileByNumber(company, selectedProfile);
    if (!profile) {
      return { ...company, [field]: value };
    }
    const profileIndex = company.perfiles?.findIndex(
      (entry) =>
        entry != null &&
        Number(entry.numeroDePerfil) === Number(selectedProfile),
    );
    if (profileIndex < 0 || !company.perfiles) {
      return { ...company, [field]: value };
    }
    const updatedPerfiles = [...company.perfiles];
    updatedPerfiles[profileIndex] = {
      ...updatedPerfiles[profileIndex],
      [field]: value,
    };
    return { ...company, perfiles: updatedPerfiles };
  }

  return { ...company, [field]: value };
};

/**
 * Whether a changed field exists in the missing required or optional maps.
 * @param {string} field
 * @param {Object|null} missingRequiredFields
 * @param {Object|null} missingOptionalFields
 * @returns {"required"|"optional"|"certifications"|"none"}
 */
export const getMissingFieldUpdateKind = (
  field,
  missingRequiredFields,
  missingOptionalFields,
) => {
  if (missingRequiredFields && Object.hasOwn(missingRequiredFields, field)) {
    return "required";
  }
  if (missingOptionalFields && Object.hasOwn(missingOptionalFields, field)) {
    return "optional";
  }
  if (field === "certificaciones") {
    return "certifications";
  }
  return "none";
};
